// ============================================================
// pages/admin/AdminBookings.jsx — Booking Management (Firebase)
// ============================================================

import React, { useState } from "react";
import { useBookings } from "../../hooks/useFirestore";
import { BookingService } from "../../services/firebaseServices";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "delivered", "cancelled"];

export default function AdminBookings({ showToast }) {
  const { bookings, loading, refresh } = useBookings(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = statusFilter === "all"
    ? bookings
    : bookings.filter((b) => b.status === statusFilter);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await BookingService.updateStatus(bookingId, newStatus, `Status updated by admin`);
      showToast(`Booking status updated to ${newStatus}`, "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to update status", "error");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Booking Management</h2>
          <p>View, confirm, and update all customer bookings</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.85rem", color: "var(--cocoa)" }}>
            {bookings.length} total bookings
          </span>
        </div>
      </div>

      {/* Status filters */}
      <div className="filters">
        <button className={`filter-btn ${statusFilter === "all" ? "active" : ""}`}
          onClick={() => setStatusFilter("all")}>
          All ({bookings.length})
        </button>
        {STATUSES.map((s) => {
          const count = bookings.filter((b) => b.status === s).length;
          return (
            <button key={s}
              className={`filter-btn ${statusFilter === s ? "active" : ""}`}
              onClick={() => setStatusFilter(s)}>
              {s.replace("_", " ")} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading bookings...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No bookings found</h3>
          <p>No bookings match the current filter.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Booking #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Date / Time</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id}>
                  <td style={{ fontWeight: 600, fontFamily: "monospace" }}>
                    {b.bookingNumber || b.id.slice(0, 8)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{b.customerName}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>
                      {b.customerEmail}
                    </div>
                    {b.customerPhone && (
                      <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>
                        {b.customerPhone}
                      </div>
                    )}
                  </td>
                  <td>
                    {b.items?.map((item, i) => (
                      <div key={i} style={{ fontSize: "0.85rem" }}>
                        {item.productName}
                        {item.selectedSize && ` (${item.selectedSize})`}
                        {item.quantity > 1 && ` ×${item.quantity}`}
                      </div>
                    )) || "—"}
                  </td>
                  <td>
                    <div>{formatDate(b.pickupDate)}</div>
                    <div style={{ fontSize: "0.82rem", color: "var(--cocoa)" }}>
                      {b.pickupTime || "—"}
                    </div>
                    <span className="inline-tag" style={{ marginTop: 4 }}>
                      {b.deliveryMethod || "pickup"}
                    </span>
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    Rs {b.total || 0}
                    {b.discount > 0 && (
                      <div style={{ fontSize: "0.75rem", color: "var(--sage-dark)" }}>
                        -{b.discount} disc.
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${b.status?.replace("_", "-")}`}>
                      {b.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td>
                    {b.status !== "cancelled" && b.status !== "delivered" && (
                      <select className="form-select"
                        style={{ fontSize: "0.82rem", padding: "0.3rem 0.5rem", minWidth: 130 }}
                        value={b.status}
                        onChange={(e) => handleStatusChange(b.id, e.target.value)}>
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s.replace("_", " ")}</option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
